/**
 * Direct-fetch wrapper for the Cloudflare Pages Function at /v1/marketing/survey.
 *
 * Keeps the same lightweight pattern as api/waitlist.ts — no external dependencies,
 * just a typed fetch call to the Pages Function endpoint.
 */

export interface SurveyPayload {
  q1_has_laundress: 'regular' | 'sometimes' | 'none';
  q2_frequency?: 'weekly' | 'biweekly' | 'monthly' | 'on_demand' | null;
  q3_monthly_pay?: 'under_2k' | '2_3k' | '3_5k' | '5_10k' | 'over_10k' | 'varies' | null;
  q4_for_whom?: 'alone' | 'whole_household' | 'partial' | null;
  q5_frustrations?: string[] | null;
  q6_household_size: '1' | '2_3' | '4_5' | '6_plus';
  q7_pay_2500: 'yes_definitely' | 'yes_maybe' | 'unsure' | 'no';
  q8_pay_4500: 'yes_definitely' | 'yes_maybe' | 'unsure' | 'no';
  q9_quartier?: string | null;
  q10_phone?: string | null;
  q10_email?: string | null;
  /** Required true only when q10_phone or q10_email is provided (H-4 consent gate). */
  consentTerms?: boolean;
  /** Cloudflare Turnstile token (H-2); sent when the widget is enabled. */
  cf_turnstile_response?: string | undefined;
  locale?: 'fr' | 'en';
}

export class SurveySubmissionError extends Error {
  readonly status: number;
  readonly field: string | undefined;

  constructor(message: string, status: number, field?: string) {
    super(message);
    this.name = 'SurveySubmissionError';
    this.status = status;
    this.field = field;
  }
}

interface SurveyErrorResponse {
  error?: string;
  field?: string;
}

interface SurveySuccessResponse {
  ok: true;
  id?: string;
}

export async function submitSurvey(payload: SurveyPayload): Promise<{ ok: boolean; id?: string }> {
  const r = await fetch('/v1/marketing/survey', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify(payload),
  });
  const body = (await r.json().catch(() => ({}))) as SurveySuccessResponse | SurveyErrorResponse;
  if (!r.ok || !('ok' in body) || body.ok !== true) {
    const errorBody = body as SurveyErrorResponse;
    throw new SurveySubmissionError(
      errorBody.error ?? 'survey_submit_failed',
      r.status,
      errorBody.field,
    );
  }
  return body;
}
